import {FC, useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {api, Announcement, DoorCode, ApiError} from '../../services/api'
import {PageHead} from './ui'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const fmt = (iso: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, {dateStyle: 'medium'})
}

const AnnouncementsPage: FC = () => {
  const [anns, setAnns] = useState<Announcement[] | null>(null)
  const [codes, setCodes] = useState<DoorCode[] | null>(null)
  const [codesLocked, setCodesLocked] = useState(false)

  useEffect(() => {
    api.publicAnnouncements().then(setAnns).catch(() => setAnns([]))
    api.doorCodes()
      .then(setCodes)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 403) setCodesLocked(true)
        setCodes([])
      })
  }, [])

  return (
    <div>
      <PageHead icon='📣' eyebrow='Stay in the loop' title='Announcements' sub='Updates from the crashpad and your entry codes.' />

      {/* Door codes */}
      <div style={{marginBottom: 32}}>
        <h2 style={{fontSize: 17, fontWeight: 800, color: INK, margin: '0 0 14px'}}>Door codes</h2>
        {codesLocked ? (
          <div style={{background: 'rgba(255,168,0,0.1)', border: '1px solid rgba(255,168,0,0.4)', borderRadius: 14, padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap'}}>
            <span style={{fontSize: 24}}>🔒</span>
            <div style={{flex: 1, minWidth: 220}}>
              <div style={{fontWeight: 700, color: '#b8770a'}}>Get verified to view door codes</div>
              <div style={{fontSize: 14, color: INK, marginTop: 2}}>Complete your renter application and an admin will approve you.</div>
            </div>
            <Link to='/apply' style={{textDecoration: 'none', fontWeight: 700, fontSize: 14, color: '#fff', background: ACCENT, borderRadius: 10, padding: '10px 18px'}}>Start application</Link>
          </div>
        ) : codes === null ? (
          <div style={{color: MUTED, fontSize: 14}}>Loading…</div>
        ) : codes.length === 0 ? (
          <div style={{background: '#fff', borderRadius: 14, padding: 24, textAlign: 'center', color: MUTED, boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}}>No door codes posted yet.</div>
        ) : (
          <div className='row g-3 ccp-stagger'>
            {codes.map((c) => (
              <div className='col-12 col-md-6 col-lg-4' key={c.id}>
                <div className='ccp-lift' style={{background: 'linear-gradient(160deg, #f1faf4, #ffffff)', border: '1px solid #e6f3ea', borderRadius: 14, padding: '18px 20px', height: '100%'}}>
                  <div style={{fontSize: 13, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em'}}>{c.label}</div>
                  <div style={{fontSize: 30, fontWeight: 800, color: ACCENT, letterSpacing: '0.05em', margin: '6px 0'}}>{c.code}</div>
                  {c.note && <div style={{fontSize: 13, color: MUTED}}>{c.note}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Announcements feed */}
      <h2 style={{fontSize: 17, fontWeight: 800, color: INK, margin: '0 0 14px'}}>Latest updates</h2>
      {anns === null ? (
        <div style={{color: MUTED, fontSize: 14}}>Loading…</div>
      ) : anns.length === 0 ? (
        <div style={{background: '#fff', borderRadius: 14, padding: 30, textAlign: 'center', color: MUTED, boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}}>No announcements yet.</div>
      ) : (
        <div className='ccp-stagger' style={{display: 'flex', flexDirection: 'column', gap: 14}}>
          {anns.map((a) => (
            <div key={a.id} className='ccp-lift' style={{background: '#fff', borderRadius: 14, padding: '18px 22px', boxShadow: '0 6px 18px rgba(27,27,41,0.06)', borderLeft: a.pinned ? `4px solid ${ACCENT}` : '4px solid transparent'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}>
                {a.pinned && <span style={{fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', borderRadius: 999, padding: '3px 8px', color: ACCENT, background: 'rgba(3,149,64,0.12)'}}>📌 Pinned</span>}
                <span style={{fontSize: 17, fontWeight: 700, color: INK}}>{a.title}</span>
              </div>
              <div style={{fontSize: 14, color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap'}}>{a.body}</div>
              <div style={{fontSize: 12, color: MUTED, marginTop: 10}}>{fmt(a.publishedAt ?? a.createdAt)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export {AnnouncementsPage}
